import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Common} from '@taopix/taopix-core';
import {
  AssetMetaInterface,
  DesignerSettings,
  AssetService,
} from '@taopix/taopix-core/dist/types/types';
import {Button, Link, Theme, ThemeName} from '@taopix/taopix-design-system';
import {Dialog, DialogContent, DialogFooter, DialogHeader} from '@taopix/taopix-design-system';
import {PictureUsage} from './AddPicturesButton';
import AssetServiceDialogAddPicsButton from './AssetServiceDialogAddPicsButton';
import APIServer from '@taopix/taopix-core/dist/types/Service/APIServer';
import AssetServiceFactory from './AssetServiceFactory';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import {useTaopix} from '../Context/TaopixContext';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import useSettings from '../../Hooks/useSettings';
import {SuccessFailList} from '../../Service/Assets/AssetHelperService';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {useUserFeedback} from '../Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../Context/UserFeedback/UserFeedbackActionTypes';
import {useAssetServices} from '../Context/AssetServices/AssetServicesContext';
import {AssetServicesActions} from '../Context/AssetServices/AssetServicesActions';
import {ExternalAssetMeta} from '../../Utils/Asset';
import useExternalServiceAPI from '../../Hooks/AssetService/useExternalServiceAPI';

/**
 * Is the service external?
 *
 * @param service
 * @returns boolean
 */
const isExternalService = (service: AssetService): boolean => {
  if (!service) {
    return false;
  }
  return (
    ['%GOOGLEPHOTOS%', '%FACEBOOK%', '%INSTAGRAM%', '%FLICKR%'].includes(service.code) ||
    true === Boolean(service.customservice)
  );
};

/**
 * Is this google photos
 *
 * @param service
 * @returns boolean
 */
const isGooglePhotos = (service: AssetService): boolean => {
  if (!service) {
    return false;
  }
  return ['%GOOGLEPHOTOS%'].includes(service.code);
};

export type AssetServiceDialogProps = {
  assetServices?: AssetService[];
  isOpen: boolean;
  serviceId: number;
  projectImageList: AssetMetaInterface[];
  pictureUsage: PictureUsage;
  closeEvent: () => void;
  onAddAssets: (assets: any) => void;
  designerSettings: DesignerSettings;
  server: APIServer;
  afterClose?: () => void;
};

const AssetServiceDialog = (props: AssetServiceDialogProps) => {
  const {
    assetServices,
    isOpen,
    serviceId,
    projectImageList,
    pictureUsage,
    closeEvent,
    afterClose,
    onAddAssets,
    designerSettings,
    server,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...rest
  } = props;
  const app = useTaopix();
  const settings = useSettings();
  const services = useAssetServices();
  const userFlow = useUserFlowState();
  const userFeedback = useUserFeedback();

  const api = useExternalServiceAPI(server);

  // pull in language support
  const {t} = useTranslation(['*', 'assetservices', 'picturemanager']);
  const ui = useUiState();

  const [loading, setLoading] = useState(false);

  const stateManager = app.core.get('stateManager');

  const clearPollingIntervals = () => {
    const pollingIntervalArray = (sessionStorage?.getItem('pollingInterval') ?? '0').split(',');
    pollingIntervalArray.forEach(interval => {
      const ref = parseInt(interval);
      if (ref > 0) {
        try {
          clearInterval(ref);
        } catch (e) {
          /* empty */
        }
      }
    });
  };

  const getServiceName = useCallback(
    (name: string | string[]): string => {
      if (Array.isArray(name)) {
        if (name[Number(ui.state.isSmallScreen)].startsWith('str_')) {
          return t(name[Number(ui.state.isSmallScreen)], {ns: 'main'});
        }
      }
      return Common.getCurrentLocaleString(name as string, true);
    },
    [ui.state.isSmallScreen]
  );

  const service = assetServices?.find(s => s.id === serviceId) as AssetService;
  const assetServiceTitle = service ? getServiceName(service.name) : '';

  const onCloseDialog = useCallback(() => {
    clearPollingIntervals();
    if (0 !== services.state.assets.length) {
      services.dispatch(AssetServicesActions.setSelectedAssets([]));
    }
    closeEvent();
  }, [closeEvent, services.state.assets]);

  const onAddExternalAssets = async (service: AssetService) => {
    setLoading(true);
    if (!isGooglePhotos(service)) {
      sessionStorage?.removeItem('googlePhotosToken');
    }

    try {
      const thing = await api.getBatchedFileSize(
        services.state.assets as unknown as ExternalAssetMeta[]
      );
      onAddAssets(thing);
      onResetSelection().then(() => {
        onCloseDialog();
        setLoading(false);
      });
    } catch (e) {
      userFeedback.dispatch(
        UserFeedbackActions.setErrorUserFeedback({
          title: '',
          message: t('*:str_ErrorCommunicationFailed'),
          showHeader: true,
        })
      );
      setLoading(false);
    }
  };

  const onAddPreviousUploadAssets = () => {
    setLoading(true);
    server
      .post<SuccessFailList>(
        '/api/assets/add',
        {
          assets: services.state.assets.map(a => a.id),
        },
        {token: 'token', json: true}
      )
      .then(({data}) => {
        // add the assets to the project
        stateManager.addAssets(services.state.assets.filter(a => data.success.includes(a.id)));
      })
      .catch(error => {
        const errorMessage = server.getErrorFromResponse(error);
        userFeedback.dispatch(
          UserFeedbackActions.setErrorUserFeedback({
            title: '',
            message: errorMessage,
            showHeader: true,
          })
        );
      })
      .finally(() => {
        onResetSelection().then(() => onCloseDialog());
        setLoading(false);
      });
  };

  const onResetSelection = async () => {
    return await new Promise((resolve, reject) => {
      services.dispatch(AssetServicesActions.resetAssets());
      resolve(true);
    });
  };

  const onAddSelectedPictures = useCallback(() => {
    if (isExternalService(service)) {
      return onAddExternalAssets(service);
    }
    onAddPreviousUploadAssets();
  }, [service, services.state.assets]);

  const TransLink = (props: {children?: string}) => {
    return (
      <Link
        href="#"
        onClick={() => {
          userFlow.activateUserFlow(UserFlowType.Auth);
          userFlow.setCurrentStep(`${UserFlowStep.Auth}.${UserFlowStep.Login}`);
        }}
      >
        {props.children || 'Sign In'}
      </Link>
    );
  };

  // sign in banner
  const signInBanner = useMemo(() => {
    if (!service || (service && service.code !== '%PREVIOUSUPLOADS%')) {
      return <></>;
    }
    if (settings.state.authentication.userId > 0 || !settings.state.authentication.canSignIn) {
      return <></>;
    }

    return (
      <Theme name={ThemeName.Prominent} className={'p-sm self-center mx-lg'} allowBorder={false}>
        <p>
          <Trans i18nKey={'assetservices:str_MessagePleaseSignIn'} t={t}>
            <TransLink />
          </Trans>
        </p>
      </Theme>
    );
  }, [settings.state.authentication.userId, service]);

  const dialogWidth = service && service.code === '%UPLOADFROMDEVICE%' ? 600 : 1000;

  return (
    <Dialog
      id="tpx-asset-services-dialog"
      open={isOpen}
      afterClose={afterClose}
      width={dialogWidth}
      theme={'tpx-theme-dialogs'}
      themeTip="Dialogs"
      title={assetServiceTitle}
      showTitle={false}
      focusFirstElement
      trapFocus
      isolateKeys={false}
      onClose={onCloseDialog}
    >
      <DialogHeader id="assetServiceDialogHeader" size={1} showSeparator={false} align="centre">
        {assetServiceTitle}
      </DialogHeader>

      {signInBanner}
      <DialogContent id="assetServiceDialogContent" className="min-h-[150px]">
        <AssetServiceFactory
          {...props}
          service={service}
          closeEvent={onCloseDialog}
          key={settings.state.authentication.userId}
        />
      </DialogContent>
      <DialogFooter isSmallScreen={ui.state.isSmallScreen}>
        <Button buttonStyle="negative" label={t('str_ButtonClose')} onClick={onCloseDialog} />
        <AssetServiceDialogAddPicsButton
          service={service}
          loading={loading}
          selectedPicsCount={services.state.assets.length}
          addSelectedPictures={onAddSelectedPictures}
        />
      </DialogFooter>
    </Dialog>
  );
};

export default AssetServiceDialog;
